import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  withStyles,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { AxiosError } from "axios";
import {
  format,
  isSameDay,
  isToday,
  isValid as isValidDate,
  setSeconds,
} from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Controller,
  FormContext,
  ManualFieldError,
  useForm,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import sortBy from "lodash/sortBy";
import { useMeterReadingMutation } from "../../api/hooks";
import { MeterPointConfig, getRate } from "../../api/meters";
import { AlertBlock } from "../../components/AlertBlock";
import { APIError } from "../../components/APIError";
import { useFallbackUrl, useNavigator } from "../../components/AppHistory";
import {
  Action,
  CancelButton,
  CloseButton,
  ConfirmButton,
  CustomDialog,
  SimpleDialog,
} from "../../components/CustomDialog";
import { InlineType } from "../../components/InlineType";
import { DialogLoader } from "../../components/Loader";
import { TextList } from "../../components/TextList";
import { UtilityIcon } from "../../components/UtilityIcon";
import { usePendingMeterReadContext } from "../../hooks";
import { useScrollTo } from "../../hooks/useScrollTo";
import { PropertyIcon } from "../../icons";
import { ModalComponent } from "../../pages/routes";
import { MeterTab } from "./components/MeterTab";
import { Register } from "./components/Register";
import { SequenceType } from "./components/SequenceType";
import { SyncTabToURL } from "./components/SyncTabToURL";
import { a11yTabProps, TabContent } from "./components/TabContent";
import { ValidationMessages } from "./components/ValidationMessages";
import { useMeterPointsForProperty } from "./hooks/useMeterPointsForProperty";
import { useProperty } from "./hooks/useProperty";
import { useTabManager } from "./hooks/useTabManager";
import { MeterReadingsContainer } from "./MeterReadingsContainer";
import {
  formatMeterValidationResults,
  FormattedMeterValidationResults,
} from "./utils/formatMeterValidationResults";
import { transformFormValuesToAPIShape } from "./utils/transformValuesToAPIShape";
import { formatDate } from "../../utils/formatDate";
import flatten from "lodash/flatten";
import {
  ValidationInfoProvider,
  useValidationLabelUtils,
} from "./ValidationInfoProvider";

export const formatDateFieldLabel = (
  date: MaterialUiPickersDate,
  invalidLabel: string,
) => {
  /* istanbul ignore else: we don't care about the elses since we're cascading returns  */
  if (isValidDate(date)) {
    if (isToday(date as Date)) {
      return `Today, ${formatDate(date as Date)}`;
    }
    return format(date as Date, "E, dd/MM/yyyy");
  }

  /* istanbul ignore next */
  return invalidLabel;
};

const MeterWarnings = withStyles(() => ({
  root: {
    "&:first-child": {
      marginTop: 0,
    },
  },
}))(Box);

const Dialog: React.FC<{ fallbackUrl?: string }> = ({ fallbackUrl }) => {
  useFallbackUrl(fallbackUrl);
  const { closeOverlay, confirmOverlay } = useNavigator();
  const { accountNumber, propertyId, meterId } = useParams();
  const { addReading } = usePendingMeterReadContext();

  // PROPERTY (for address)
  const {
    data: property,
    error: propertyError,
    status: propertyStatus,
  } = useProperty({
    accountNumber: accountNumber!,
    propertyId: propertyId,
  });

  const readingDttm = useRef(setSeconds(new Date(), 0));
  const {
    data: meterPointConfigs,
    status: meterPointConfigsStatus,
    error: meterPointConfigsError,
  } = useMeterPointsForProperty(propertyId, accountNumber, readingDttm.current);

  const { saveReading, error: saveError } = useMeterReadingMutation(
    accountNumber!,
  );

  const {
    activeTabId,
    setActiveTabId,
    tabIds,
    tabIdFromMeterPointMap,
  } = useTabManager(meterPointConfigs ?? []);

  // If after a date change, the previously selected meter is no longer available
  // switch to the first tab of the newly updated meter configs.
  useEffect(() => {
    if (activeTabId && tabIds.length && !tabIds.includes(activeTabId)) {
      setActiveTabId(tabIds[0]);
    }
  }, [activeTabId, meterId, meterPointConfigs, setActiveTabId, tabIds]);

  const formMethods = useForm({ mode: "onBlur" });
  const {
    handleSubmit,
    register,
    formState,
    control,
    errors,
    setError,
    getValues,
    triggerValidation,
    reset,
  } = formMethods;

  const { getLabel } = useValidationLabelUtils();

  const validationMessages: FormattedMeterValidationResults = useMemo(() => {
    const err = saveError as AxiosError;
    if (err && err?.response) {
      /* istanbul ignore else: no need to handle anything else here */
      if (err.response.status === 400) {
        try {
          const validationResults = formatMeterValidationResults(
            err.response.data.results,
          );

          const prependLabel = (path: string) => (msg: string) => {
            const label = getLabel(path);
            return label ? `${label}: ${msg}` : msg;
          };

          // TODO: Refactor to move out of this file and into generic util
          const resultsWithLabels: FormattedMeterValidationResults = Object.entries(
            validationResults,
          ).reduce(
            (acc, [meterId, results]) => ({
              ...acc,
              [meterId]: {
                ...results,
                errors: Object.entries(results.errors).reduce(
                  (errs, [path, msgs]) => ({
                    ...errs,
                    [path]: getLabel(path)
                      ? msgs.map(prependLabel(path))
                      : msgs,
                  }),
                  {},
                ),
                warnings: Object.entries(results.warnings).reduce(
                  (warns, [path, msgs]) => ({
                    ...warns,
                    [path]: getLabel(path)
                      ? msgs.map(prependLabel(path))
                      : msgs,
                  }),
                  {},
                ),
              },
            }),
            {},
          );

          // Programmatically set errors on the form
          const formErrorsByField = Object.values(resultsWithLabels).reduce(
            (acc: ManualFieldError<any>[], types) => {
              return [
                ...acc,
                ...Object.entries(types.errors)
                  // this filter ensures we're not creating multiple FieldError object with the same path
                  // if in the future we want to map messages to fields, we would need to remove this and
                  // instead merge the messages with the existing FieldError object
                  .filter(([path]) => !acc.some((entry) => entry.name === path))
                  .map(([path]) => ({
                    name: path,
                    type: "API_Error",
                  })),
                // We're not outlining/highlighting Warnings in the UI
                // ...Object.entries(types.warnings).map(([path, errs]) => ({
                //   name: path,
                //   type: "API_Warning",
                // })),
              ];
            },
            [],
          );

          const hasAnyErrors = Object.values(resultsWithLabels).some(
            (result) => result.hasErrors,
          );

          if (hasAnyErrors) {
            setError(formErrorsByField);
          }

          return resultsWithLabels;
        } catch {
          // ignore
        }
      }
    }
    return {};
  }, [saveError, getLabel, setError]);

  // Do ANY warnings exist on any tabs?
  const hasOnlyWarnings = useMemo(() => {
    const results = Object.values(validationMessages);
    const hasWarnings = results.some((result) => result.hasWarnings);
    const hasErrors = results.some((result) => result.hasErrors);

    return hasWarnings && !hasErrors;
  }, [validationMessages]);

  const hasAcceptedWarnings = useRef(false);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);

  const hasAnyErrors = useMemo(() => {
    const results = Object.values(validationMessages);
    return results.some((result) => result.hasErrors);
  }, [validationMessages]);

  // When we have ONLY warnings and no errors, then display the overlay
  useEffect(() => {
    hasAcceptedWarnings.current = false;
    setIsWarningDialogOpen(hasOnlyWarnings);
  }, [hasOnlyWarnings]);

  const cachedFormValues = useRef(getValues());

  // React to NEW meterpoint configs being loaded in after a date change
  useEffect(() => {
    // Stripping out the "readingDttm" since we don't want to reset this back to the initial date
    const { readingDttm, ...values } = cachedFormValues.current;
    reset(values, {
      errors: true,
      touched: true,
      dirtyFields: true,
      dirty: true,
    });
  }, [meterPointConfigs, cachedFormValues, reset]);

  const onDateConfirmed = useCallback(() => {
    const formValues = getValues();
    const date = formValues.readingDttm as Date;

    /* istanbul ignore else */
    if (!isSameDay(readingDttm.current, date)) {
      readingDttm.current = date; // update the reference value
      cachedFormValues.current = formValues;
    }
  }, [getValues, readingDttm, cachedFormValues]);

  const validateTab = useCallback(
    async (tabId: string) => {
      const fields = getValues();
      const tabFields = Object.keys(fields).filter((path) =>
        path.startsWith(tabId),
      );
      /* istanbul ignore else */
      if (tabFields?.length) {
        await triggerValidation(tabFields);
      }
    },
    [getValues, triggerValidation],
  );

  const onTabChange = useCallback(
    async (e, selected) => {
      // Update active tab id which will switch tab
      setActiveTabId(selected);
      await validateTab(activeTabId as string);
    },
    [setActiveTabId, activeTabId, validateTab],
  );

  const tabRef = useRef(activeTabId);
  useEffect(() => {
    if (activeTabId && activeTabId !== tabRef.current) {
      const previousTab = tabRef.current as string;

      (async () => {
        await validateTab(previousTab);
      })();

      tabRef.current = activeTabId;
    }
  }, [activeTabId, tabRef, validateTab]);

  // Handle the auto-focus of tabs as a result of a Server Side API error / warning
  useEffect(() => {
    // Focus on the first error tab if there are ANY errors in the API response
    if (hasAnyErrors) {
      const focusErrorId = tabIds.reduce((id: null | string, tabId) => {
        return !id && validationMessages[tabId]?.hasErrors ? tabId : id;
      }, null);
      if (focusErrorId) setActiveTabId(focusErrorId);
    }
  }, [hasAnyErrors, setActiveTabId, tabIds, validationMessages]);

  useEffect(() => {
    // Focus on the first warning tab if there are ONLY warnings in the API response
    if (hasOnlyWarnings) {
      const focusWarningId = tabIds.reduce((id: null | string, tabId) => {
        return !id && validationMessages[tabId]?.hasWarnings ? tabId : id;
      }, null);
      if (focusWarningId) setActiveTabId(focusWarningId);
    }
  }, [hasOnlyWarnings, setActiveTabId, tabIds, validationMessages]);

  const meterPointFromMeterId = useCallback(
    (meterId: string) => {
      const meterPointId = Object.entries(tabIdFromMeterPointMap).reduce(
        (id: null | string, [meterPointId, meterIds]) =>
          meterIds.includes(meterId) ? meterPointId : id,
        null,
      );

      if (!meterPointId) {
        throw new Error(
          `Couldn't find a meter point belonging to the meter id (${meterId}).`,
        );
      }

      return meterPointConfigs?.find((config) => config.id === meterPointId);
    },
    [meterPointConfigs, tabIdFromMeterPointMap],
  );

  const onSubmit = useCallback(
    async (values) => {
      const isValid = await triggerValidation();

      /* istanbul ignore else */
      if (isValid) {
        const payload = transformFormValuesToAPIShape(values);
        const response = await saveReading(
          payload,
          hasAcceptedWarnings.current,
        );

        if (response?.status === 201) {
          const updatedMeterIds = payload.map((meter: any) => meter.id);
          const updatedMeterPoints: Record<
            string,
            MeterPointConfig
          > = updatedMeterIds.reduce((acc, meterId) => {
            const meterPoint = meterPointFromMeterId(meterId);
            return meterPoint
              ? {
                  ...acc,
                  [meterPoint.id]: meterPoint,
                }
              : acc;
          }, {});

          // Add a reading PER meterpoint and collect success message parts
          const messages: string[] = [];
          Object.values(updatedMeterPoints).forEach((meterPoint) => {
            messages.push(`${meterPoint.type} ${meterPoint.identifier}`);
            addReading(meterPoint.id, new Date());
          });

          confirmOverlay(`/accounts/${accountNumber}`, {
            success: `Meter reading added for ${messages.join(", ")}`,
          });
        }
      }
    },
    [
      triggerValidation,
      saveReading,
      confirmOverlay,
      accountNumber,
      meterPointFromMeterId,
      addReading,
    ],
  );

  // If the form is not in a valid state, we detect and activate the first
  // error tab so that the natural submit lifecycles can focus the correct
  // form field afterwards
  const onFormSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
        e.persist();
      }

      const isValid = await triggerValidation();

      /* istanbul ignore else */
      if (!isValid) {
        const tabId = tabIds.reduce((eref: any, tabId: string) => {
          const meterErrors = errors[tabId];

          if (eref) return eref;

          if (meterErrors) {
            const ref = meterErrors?.readings?.reduce(
              (r: any, reading: any) => {
                return reading.cumulative ? reading.cumulative : r;
              },
              null,
            );

            if (ref) {
              return tabId;
            }
            return eref;
          }

          return eref;
        }, null);

        if (tabId) {
          setActiveTabId(tabId);
        }
      }

      await handleSubmit(onSubmit)(e);
    },
    [triggerValidation, handleSubmit, onSubmit, tabIds, errors, setActiveTabId],
  );

  const handleWarningDialogContinue = useCallback(async () => {
    hasAcceptedWarnings.current = true;
    setIsWarningDialogOpen(true);
    await onFormSubmit();
  }, [onFormSubmit]);

  const handleWarningDialogDismiss = useCallback(() => {
    hasAcceptedWarnings.current = false;
    setIsWarningDialogOpen(false);
  }, []);

  const address = Object.values(property?.address ?? {})
    .filter((value) => value && value.length)
    .join(", ");

  const fetchStatuses = [meterPointConfigsStatus, propertyStatus];

  const fetchErrorTitle = useMemo(() => {
    let title;

    if (meterPointConfigsError) {
      title = "We could not retrieve this meter point's configuration.";
    }
    if (propertyError) {
      title = "We could not find a property that matches this meter point.";
    }

    return title;
  }, [meterPointConfigsError, propertyError]);

  const [scrollableContentRef, handleErrorAppearing] = useScrollTo();

  const { isSubmitting } = formState;

  return (
    <FormContext {...formMethods}>
      <CustomDialog
        maxWidth="md"
        onClose={closeOverlay}
        data-testid="MeterPointDialog"
        PaperProps={{
          component: "form",
          onSubmit: onFormSubmit,
          // @ts-ignore
          noValidate: true,
          style: {
            height: "100%",
          },
        }}
      >
        <DialogTitle
          disableTypography={true}
          data-testid="MeterPointDialog__Title"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="no-wrap"
          >
            <Box>
              <Typography variant="h5" variantMapping={{ h5: "h2" }}>
                Add meter reading
              </Typography>
              <Box display="flex" alignItems="center">
                <Box mr={0.5} lineHeight={1}>
                  <PropertyIcon height={16} width={16} />
                </Box>
                <Typography>{address}</Typography>
              </Box>
            </Box>
            <Box mr={-1.5}>
              <CloseButton data-testid="MeterPointDialog__Button--close" />
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          data-testid="MeterPointDialog__Content"
          ref={scrollableContentRef}
        >
          {fetchStatuses.includes("loading") ? (
            <DialogLoader />
          ) : fetchStatuses.includes("error") ? (
            <APIError onMount={handleErrorAppearing} title={fetchErrorTitle} />
          ) : meterPointConfigs ? (
            <>
              <Grid
                container
                direction="row"
                justify="flex-start"
                alignItems="flex-start"
                spacing={4}
              >
                <Grid item xs={12}>
                  <Controller
                    data-testid="MeterPointDialog__FormField--date"
                    name="readingDttm"
                    as={KeyboardDatePicker}
                    autoOk={true}
                    disableFuture={true}
                    disableToolbar={true}
                    format="E, dd/MM/yyyy"
                    labelFunc={formatDateFieldLabel}
                    variant="inline"
                    label="Reading Date"
                    inputVariant="outlined"
                    InputProps={{
                      /* This ensures a user cannot manually try an enter a date value which causes massive issues */
                      readOnly: true,
                    }}
                    onClose={onDateConfirmed}
                    control={control}
                    rules={{
                      required: "A valid date selection is required.",
                    }}
                    required
                    // NOTE: This ensures that API errors do not highlight the field. This is a trial of whether this works well with customers.
                    // error={!!errors.readingDttm}
                    // helperText={errors.readingDttm?.message}
                    defaultValue={readingDttm.current}
                    value=""
                  />
                </Grid>
                <Grid item xs={12}>
                  <Tabs
                    value={activeTabId}
                    onChange={onTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {meterPointConfigs.map((meterPoint) => {
                      return meterPoint.meters.map((meter) => (
                        <Tab
                          {...a11yTabProps(meter.id)}
                          key={meter.id}
                          value={meter.id}
                          title={meter.id}
                          data-testid={`MeterPointDialog__Tab--${meter.id}`}
                          label={
                            <MeterTab
                              hasAPIError={
                                !!validationMessages?.[meter.id]?.hasErrors
                              }
                              hasAPIWarning={
                                !!validationMessages?.[meter.id]?.hasWarnings
                              }
                              utilityType={meterPoint.type}
                              meterPointIdentifier={meterPoint.identifier}
                              meterId={meter.id}
                              meterIdentifier={meter.identifier}
                              registerCount={meter.registers.length}
                            />
                          }
                        />
                      ));
                    })}
                  </Tabs>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  {activeTabId &&
                    meterPointConfigs.map((meterPoint) =>
                      meterPoint.meters.map((meter) => (
                        <TabContent
                          key={meter.id}
                          id={meter.id}
                          activeId={activeTabId}
                        >
                          {meterPoint.isHalfHourly ? (
                            <AlertBlock
                              data-testid="MeterPointDialog__Warning--HalfHourly"
                              variant="info"
                              messages={[
                                "This is a half-hourly meter. Meter reading can't be added for this meter type.",
                              ]}
                              isClosable={false}
                            />
                          ) : (
                            <Box>
                              {meter.id === activeTabId && (
                                <SyncTabToURL
                                  meterPointId={meterPoint.id}
                                  meterId={meter.id}
                                />
                              )}
                              <input
                                ref={register}
                                type="hidden"
                                name={`${meter.id}.id`}
                                value={meter.id}
                              />

                              {/* Error or Warning Messages */}
                              {saveError && validationMessages?.[meter.id] && (
                                <Box mb={2} mt={0}>
                                  <ValidationMessages
                                    meterId={meter.id}
                                    messages={validationMessages}
                                    onMount={handleErrorAppearing}
                                  />
                                </Box>
                              )}

                              <Box mb={5}>
                                <SequenceType
                                  name={`${meter.id}.sequenceType`}
                                />
                              </Box>
                              <Box>
                                {meter.registers.length > 1 && (
                                  <Typography>Readings</Typography>
                                )}{" "}
                                {sortBy(meter.registers, (r) => getRate(r)).map(
                                  (register, i) => (
                                    <Register
                                      data-testid="MeterPointDialog__FormField--register"
                                      key={register.id}
                                      meterId={meter.id}
                                      activeTabId={activeTabId}
                                      fieldNamePrefix={`${meter.id}`}
                                      label={
                                        meter.registers.length === 1
                                          ? "Reading"
                                          : `Rate: ${getRate(register)}`
                                      }
                                      index={i}
                                      autofocus={false}
                                      config={register}
                                    />
                                  ),
                                )}
                              </Box>
                              <Box mt={6}>
                                <MeterReadingsContainer meter={meter} />
                              </Box>
                            </Box>
                          )}
                        </TabContent>
                      )),
                    )}
                </Grid>
              </Grid>
            </>
          ) : /* istanbul ignore next */ null}
        </DialogContent>
        <DialogActions data-testid="MeterPointDialog__Actions">
          <CancelButton data-testid="MeterPointDialog__Button--cancel" />
          <ConfirmButton
            data-testid="MeterPointDialog__Button--submit"
            type="submit"
            disabled={isSubmitting}
          />
        </DialogActions>
      </CustomDialog>

      {/* Renders on top of the other dialog */}
      <SimpleDialog
        data-testid="MeterPointWarningDialog"
        title="Please review"
        variant="info"
        open={isWarningDialogOpen}
        actions={[
          <Action
            onClick={handleWarningDialogContinue}
            variant="primary"
            label="Continue"
          />,
          <Action
            onClick={handleWarningDialogDismiss}
            label="Change reading"
          />,
        ]}
      >
        {meterPointConfigs?.map((meterPoint) =>
          meterPoint.meters.map((meter) => {
            /* istanbul ignore else: the return means there is no else */
            if (!validationMessages?.[meter.id]?.hasWarnings) return null;

            const meterFieldWarnings = Object.values(
              validationMessages?.[meter.id]?.warnings,
            );
            const allWarnings: string[] = flatten(meterFieldWarnings);

            return (
              <MeterWarnings key={`${meterPoint.id}_${meter.id}`} mt={5}>
                {/* Header */}
                <Box display="flex" alignItems="center" mb={1}>
                  <UtilityIcon type={meterPoint.type} height={16} width={16} />
                  <Box ml={1}>
                    <Typography>
                      <InlineType fontWeight="bold">
                        {meterPoint.type} {meterPoint.identifier},{" "}
                        {meter.identifier}
                      </InlineType>
                    </Typography>
                  </Box>
                </Box>
                {/* Warning Message */}
                <Box>
                  {allWarnings.length > 1 ? (
                    <TextList dense={true} ml={1}>
                      {allWarnings.map((warning, i) => (
                        <li key={i}>
                          <Typography>{warning}</Typography>
                        </li>
                      ))}
                    </TextList>
                  ) : (
                    /* istanbul ignore next: it's too hard to mock just 1 warning */
                    allWarnings.map((warning, i) => (
                      /* istanbul ignore next */
                      <Typography key={i}>{warning}</Typography>
                    ))
                  )}
                </Box>
              </MeterWarnings>
            );
          }),
        )}
      </SimpleDialog>
    </FormContext>
  );
};

export const MeterPointDialog: ModalComponent = ({ fallbackUrl }) => (
  <ValidationInfoProvider>
    <Dialog fallbackUrl={fallbackUrl} />
  </ValidationInfoProvider>
);
